"use client";
import LayoutBanner from "@/components/customLayoutServices/LayoutBanner";
import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { MapPin, Clock, Bus, DoorOpen } from "lucide-react";
import Image from "next/image";
import dynamic from "next/dynamic";
import {
  externoBuses,
  externoRoute,
  externoStops,
  EXTERNO_INTERVAL_MINUTES,
  getPanamaMinutesOfDay,
  internoBuses,
  internoRoute,
  internoStops,
  INTERNO_INTERVAL_MINUTES,
} from "./components/shuttleRoutes";

const FOTO_INTERNO =
  "https://galeria.tocumenaero.com/gallery/servicios/shuttle_interno1.JPG";
const FOTO_EXTERNO =
  "https://galeria.tocumenaero.com/gallery/servicios/shuttle_externo1.JPG";

const ShuttleRouteMap = dynamic(() => import("./components/ShuttleRouteMap"), {
  ssr: false,
});

type Ubicacion = { terminal: string; detalle: string };

export default function ShuttlePage() {
  // Antes el locale se leía de window.location dentro de un useEffect, lo que
  // renderizaba español un instante antes de corregirse.
  const t = useTranslations("shuttlePage");
  const tCommon = useTranslations("common");

  const interno = {
    titulo: t("interno.titulo"),
    descripcion: t("interno.descripcion"),
    horario: t("interno.horario"),
    foto: FOTO_INTERNO,
    ubicaciones: t.raw("interno.ubicaciones") as Ubicacion[],
  };
  const externo = {
    titulo: t("externo.titulo"),
    descripcion: t("externo.descripcion"),
    horario: t("externo.horario"),
    foto: FOTO_EXTERNO,
    ubicaciones: t.raw("externo.ubicaciones") as Ubicacion[],
  };
  const data = { interno, externo };

  return (
    <div className="pb-30">
      <LayoutBanner
        cardTitle={t("bannerTitle")}
        bannerSrc="https://galeria.tocumenaero.com/gallery/servicios/shuttle_interno1.JPG"
      >
        <>
          <section className="w-full max-w-6xl mx-auto px-4 pt-8">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl shadow-xl p-4 md:p-5 border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <Bus className="w-5 h-5 text-[#102542]" />
                  <h3 className="text-lg md:text-xl font-bold text-[#102542]">
                    {t("rutaInterna")}
                  </h3>
                </div>
                <NextShuttleCountdown
                  intervalMinutes={INTERNO_INTERVAL_MINUTES}
                />
                <ShuttleRouteMap
                  route={internoRoute}
                  stops={internoStops}
                  color="#102542"
                  buses={internoBuses}
                />
              </div>

              <div className="bg-white rounded-2xl shadow-xl p-4 md:p-5 border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <Bus className="w-5 h-5 text-[#102542]" />
                  <h3 className="text-lg md:text-xl font-bold text-[#102542]">
                    {t("rutaExterna")}
                  </h3>
                </div>
                <NextShuttleCountdown
                  intervalMinutes={EXTERNO_INTERVAL_MINUTES}
                />
                <ShuttleRouteMap
                  route={externoRoute}
                  stops={externoStops}
                  color="#102542"
                  buses={externoBuses}
                />
              </div>
            </div>
          </section>

          <section className="w-full max-w-6xl mx-auto px-4 py-12">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="group bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 border border-gray-100">
                <div className="relative h-56 overflow-hidden">
                  <Image
                    src={data.interno.foto}
                    alt={data.interno.titulo}
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center gap-2 text-white">
                      <Bus className="w-6 h-6" />
                      <h3 className="text-xl font-bold">
                        {data.interno.titulo}
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {data.interno.descripcion}
                  </p>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-[#102542] font-semibold">
                      <MapPin className="w-5 h-5" />
                      <span>{tCommon("ubicacion")}</span>
                    </div>
                    <div className="space-y-2 pl-7">
                      {data.interno.ubicaciones.map((ub, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <DoorOpen className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-600">
                            <span className="font-medium text-gray-800">
                              {ub.terminal}:
                            </span>{" "}
                            {ub.detalle}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 bg-[var(--fondoGeneral-secciones)]/10 rounded-lg p-3">
                    <Clock className="w-5 h-5 text-[var(--fondoGeneral-secciones)]" />
                    <p className="text-sm font-medium text-[var(--fondoGeneral-secciones)]">
                      {data.interno.horario}
                    </p>
                  </div>
                </div>
              </div>

              <div className="group bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 border border-gray-100">
                <div className="relative h-56 overflow-hidden">
                  <Image
                    src={data.externo.foto}
                    alt={data.externo.titulo}
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center gap-2 text-white">
                      <Bus className="w-6 h-6" />
                      <h3 className="text-xl font-bold">
                        {data.externo.titulo}
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {data.externo.descripcion}
                  </p>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-[var(--fondoGeneral-secciones)] font-semibold">
                      <MapPin className="w-5 h-5" />
                      <span>{tCommon("ubicacion")}</span>
                    </div>
                    <div className="space-y-2 pl-7">
                      {data.externo.ubicaciones.map((ub, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <DoorOpen className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-600">
                            <span className="font-medium text-gray-800">
                              {ub.terminal}:
                            </span>{" "}
                            {ub.detalle}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 bg-[var(--fondoGeneral-secciones)]/10 rounded-lg p-3">
                    <Clock className="w-5 h-5 text-[var(--fondoGeneral-secciones)]" />
                    <p className="text-sm font-medium text-[var(--fondoGeneral-secciones)]">
                      {data.externo.horario}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      </LayoutBanner>
    </div>
  );
}

const SERVICE_START_HOUR = 5;
const SERVICE_END_HOUR = 23;

function NextShuttleCountdown({ intervalMinutes }: { intervalMinutes: number }) {
  const tShuttle = useTranslations("shuttlePage");
  const [now, setNow] = useState<Date | null>(null);

  useEffect(() => {
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!now) {
    return <div className="h-24 mb-4" />;
  }

  const t = {
    next: tShuttle("countdown.next"),
    in: tShuttle("countdown.in"),
    every: tShuttle("countdown.every", { minutes: intervalMinutes }),
    closed: tShuttle("countdown.closed"),
    reopens: tShuttle("countdown.reopens"),
  };

  const nowMinutes = getPanamaMinutesOfDay(now);
  const startMinutes = SERVICE_START_HOUR * 60;
  const endMinutes = SERVICE_END_HOUR * 60;

  let nextSlotMinutes: number | null = null;
  let isClosed = false;

  if (nowMinutes < startMinutes) {
    nextSlotMinutes = startMinutes;
    isClosed = true;
  } else if (nowMinutes >= endMinutes) {
    nextSlotMinutes = startMinutes + 24 * 60;
    isClosed = true;
  } else {
    const slot =
      (Math.floor(nowMinutes / intervalMinutes) + 1) * intervalMinutes;
    if (slot > endMinutes) {
      nextSlotMinutes = startMinutes + 24 * 60;
      isClosed = true;
    } else {
      nextSlotMinutes = slot;
    }
  }

  const diffSec = Math.max(0, Math.round((nextSlotMinutes - nowMinutes) * 60));
  const mm = Math.floor(diffSec / 60);
  const ss = diffSec % 60;

  const reopenHour = Math.floor((nextSlotMinutes % (24 * 60)) / 60);
  const reopenMinute = nextSlotMinutes % 60;
  const reopenLabel = `${((reopenHour + 11) % 12) + 1}:${String(
    reopenMinute,
  ).padStart(2, "0")} ${reopenHour < 12 ? "a.m." : "p.m."}`;

  if (isClosed) {
    return (
      <div className="mb-4 rounded-2xl border-2 border-[#102542]/15 bg-linear-to-br from-white to-gray-50 p-4 shadow-sm">
        <div className="flex items-center gap-2 text-gray-500 mb-2">
          <Clock className="w-4 h-4 shrink-0" />
          <span className="text-xs font-semibold uppercase tracking-wide">
            {t.closed}
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-sm text-gray-600">{t.reopens}</span>
          <span className="text-2xl font-bold text-[#102542] tabular-nums">
            {reopenLabel}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-2xl border-2 border-[#102542]/20 bg-white p-4 shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-1.5 text-[#102542]/70 mb-1">
            <Clock className="w-3.5 h-3.5 shrink-0" />
            <span className="text-[11px] font-semibold uppercase tracking-wider">
              {t.next}
            </span>
          </div>
          <span className="text-[11px] text-gray-500 font-medium">
            {t.every}
          </span>
        </div>
        <div className="flex flex-col items-end shrink-0">
          <span className="text-[10px] font-medium text-[#102542]/60 uppercase tracking-wide">
            {t.in}
          </span>
          <span className="text-4xl md:text-5xl font-black text-[#102542] tabular-nums leading-none mt-0.5">
            {String(mm).padStart(2, "0")}
            <span className="text-[#102542]/40">:</span>
            {String(ss).padStart(2, "0")}
          </span>
        </div>
      </div>
    </div>
  );
}
